import { useMemo } from 'react';
import {
	BigNum,
	PERCENTAGE_PRECISION_EXP,
	QUOTE_PRECISION_EXP,
} from '@drift-labs/sdk';
import { PortfolioPeriod } from 'src/components/PortfolioPage/types';
import useAppInitialLoad from 'src/hooks/app/useAppInitialLoad';
import useLatchedValue from 'src/hooks/utils/useLatchedValue';
import usePortfolioTradingSnapshots from 'src/components/PortfolioPage/hooks/usePortfolioTradingSnapshots';
import roundPortfolioTradeValue from 'src/components/PortfolioPage/utils/roundPortfolioTradeValue';
import computePortfolioTradeDelta from 'src/components/PortfolioPage/utils/computePortfolioTradeDelta';
import { PortfolioFilledTradingSnapshot } from 'src/components/PortfolioPage/utils/fillMissingPortfolioSnapshots';

interface Params {
	period: PortfolioPeriod;
	netAccountValue: number | undefined;
}

export default function usePortfolioHeaderData({
	period: periodFromProps,
	netAccountValue: netAccountValueFromProps,
}: Params) {
	const { isInitialLoading } = useAppInitialLoad();

	const { data, isLoading, ...rest } = usePortfolioTradingSnapshots({
		period: periodFromProps,
		maxSnapshots: 90,
		fillStrategy: 'repeatLastKnown',
	});

	const netAccountValue = useLatchedValue({
		value: netAccountValueFromProps,
		isLatched: rest.isPlaceholderData,
	});

	const baseData = useMemo(() => {
		return buildBaseData({ snapshots: data?.snapshots });
	}, [data]);

	const liveData = useMemo(() => {
		return buildLiveData({ baseData, netAccountValue });
	}, [baseData, netAccountValue]);

	const processedData = useMemo(() => {
		if (!baseData.ready || !netAccountValue) {
			return {
				accountValueChange: undefined,
				accountValueChangePct: undefined,
				miniChartData: [],
				expandedChartData: [],
			};
		}

		return {
			accountValueChange: liveData.accountValueChange,
			accountValueChangePct: liveData.accountValueChangePct,
			expandedChartData: [
				...baseData.expandedChartSeries,
				liveData.expandedChartLivePoint,
			],
			miniChartData: [...baseData.miniChartSeries, netAccountValue],
		};
	}, [baseData, liveData, netAccountValue]);

	return {
		data: processedData,
		isLoading: isLoading || isInitialLoading,
		...rest,
	};
}

interface BuildBaseDataParams {
	snapshots: PortfolioFilledTradingSnapshot[] | undefined;
}

function buildBaseData({ snapshots }: BuildBaseDataParams) {
	if (!snapshots?.length) {
		return {
			miniChartSeries: [],
			expandedChartSeries: [],
			startOfPeriodSnapshot: undefined,
			ready: false,
		};
	}

	const startOfPeriodSnapshot = snapshots[0];

	const miniChartSeries = snapshots.map((snapshot) =>
		roundPortfolioTradeValue(snapshot.accountBalance)
	);

	const expandedChartSeries = snapshots.map((snapshot) => ({
		date: new Date(snapshot.ts * 1000),
		accountValue: roundPortfolioTradeValue(snapshot.accountBalance),
		isMockedSnapshot: !!snapshot.isMockedSnapshot,
	}));

	return {
		miniChartSeries,
		expandedChartSeries,
		startOfPeriodSnapshot,
		ready: true,
	};
}

interface BuildLiveDataParams {
	baseData: ReturnType<typeof buildBaseData>;
	netAccountValue: number | undefined;
}

function buildLiveData({ baseData, netAccountValue }: BuildLiveDataParams) {
	const expandedChartLivePoint = {
		date: new Date(),
		accountValue: netAccountValue,
		isMockedSnapshot: false,
	};

	if (!baseData.ready || !netAccountValue) {
		return {
			accountValueChange: undefined,
			accountValueChangePct: undefined,
			expandedChartLivePoint,
		};
	}

	const { accountValueChange, accountValueChangePct } = (() => {
		const startOfPeriodAccountValue =
			baseData.startOfPeriodSnapshot?.accountBalance ?? 0;

		const delta = computePortfolioTradeDelta(
			netAccountValue,
			startOfPeriodAccountValue
		);

		const changePct =
			startOfPeriodAccountValue >= 0.1
				? (delta / startOfPeriodAccountValue) * 100
				: // Should be either -100 or 100 depending on the diff.
					100 * (delta / delta);

		return {
			accountValueChange: BigNum.fromPrint(
				delta.toString(),
				QUOTE_PRECISION_EXP
			),
			accountValueChangePct: BigNum.fromPrint(
				changePct.toString(),
				PERCENTAGE_PRECISION_EXP
			),
		};
	})();

	return {
		accountValueChange,
		accountValueChangePct,
		expandedChartLivePoint,
	};
}
